// ANGULAR
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
// SERVICES
import { AlertService } from '@services/alert.service';
import { AuthService } from '@api-requests-services/auth.service';
import { UsuariosService } from '@api-requests-services/usuarios.service';
// MODELS
import { PostVerifyAuthRequest } from '@models/auth.model';
// STORE
import { Store } from '@ngrx/store';
import { MainState } from '@store/main.reducer';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { updateError, updateLoginInProcess, updateVerifyTotpInProcess } from '@store/app/app.actions';
import {
  loginSuccess, loginError, login, logout, logoutSuccess, logoutError,
  updateTokens, loginWait, changePassword, changePasswordError, resetError,
  recoverPassword, recoverPasswordSuccess, recoverPasswordError,
  refreshTokenSuccess, refreshTokenError, updateAuthMethods, 
  updateTotpRegisterAuth, verifyTotp, verifyMethodError,  
} from '@store/session/session.actions';
// RXJS
import { of } from 'rxjs';
import { catchError, first, map, switchMap, tap } from 'rxjs/operators';
// OTHERS
import Swal from 'sweetalert2';
import { Paths } from '@constants/paths.const';
import { refreshL, tokenL } from '@constants/e.const';

@Injectable()
export class SessionEffects {

  constructor(
    private alertService: AlertService,
    private authService: AuthService,
    private usuariosService: UsuariosService,
    private actions$: Actions,
    private store: Store<MainState>,
    private router: Router,
  ) { }

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(login),
      // tap(action => console.log('Action received in Effect:', action)),
      switchMap(action =>
        this.authService.postLogin(action.postLoginRequest).pipe(
          first(),
          map(
            response => {
              //TOTP obligatorio
              if (!response.accessToken && !!response.authMethods?.preAuthToken) {
                this.store.dispatch(updateTokens({ token: response.authMethods.preAuthToken, refreshToken: "" })); // Token provisional
                return updateAuthMethods({ authMethods: response.authMethods });
              } else {
                //Guardamos el token de usuario
                if (!!response.accessToken && !!response.refreshToken) {
                  this.store.dispatch(updateTokens({ token: response.accessToken, refreshToken: response.refreshToken }));
                }
                this.store.dispatch(resetError());

                //Si se requiere cambio de contraseña
                if (!!response.cambioPassword) {
                  // Especial: showConfirmAlert sin icono ni título, con HTML, y cierre controlado (allowOutsideClick)
                  Swal.fire({
                    icon: 'info',
                    html: response.cambioPassword.motivo,
                    showConfirmButton: true,
                    confirmButtonText: "Aceptar",
                    confirmButtonColor: 'var(--primary-color)',
                    showDenyButton: true,
                    denyButtonColor: 'var(--false-color)',
                    denyButtonText: "Cancelar",
                    showCancelButton: false,
                    allowOutsideClick: false,
                    reverseButtons: true
                  }).then(
                    (result) => {
                      if (result.isConfirmed) {
                        this.router.navigate([Paths.changePasswordRequired]);
                      } else if (result.isDenied) {
                        this.store.dispatch(logoutSuccess());
                      }
                    }
                  );
                  return loginWait({ user: response });
                } else { //En cualquier otro caso, recuperamos las cuentas del usuario y damos el login por exitoso
                  return loginSuccess({ user: response });
                }
              }
            }
          ),
          catchError(
            error => {
              // console.log(error);
              this.store.dispatch(updateLoginInProcess({ loginInProcess: false }));
              return of(loginError({ error }));
            }
          )
        )
      )
    )
  );

  // Efecto para manejar el login exitoso
  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginSuccess),
      tap(
        action => {
          // console.log('User:', action.user);
          this.store.dispatch(updateLoginInProcess({ loginInProcess: false }));
          this.store.dispatch(updateVerifyTotpInProcess({ verifyTotpInProcess: false }));
          if (!!action.user.accessToken && !!action.user.refreshToken) {
            this.store.dispatch(updateTokens({ token: action.user.accessToken, refreshToken: action.user.refreshToken }));
          }
          this.router.navigate([Paths.home]);
        }
      )
    ),
    { dispatch: false }
  );

  loginError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loginError),
      map(action => updateError({ error: action.error }))
    ),
  );

  // Efecto para manejar la validación Totp
  updateAuthMethods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateAuthMethods),
      tap(
        action => {
          const totpMethod: string | null = action.authMethods?.unregistered?.find(method => method == "Totp") ?? null;

          if (!!totpMethod) {
            this.authService.postGetMethods(totpMethod).subscribe(
              response => {
                if (!!response.totp) {
                  this.store.dispatch(updateTotpRegisterAuth({ totpRegisterAuth: response.totp }));
                }
              },
              (error) => {
                this.alertService.swalError(error.error);
                console.error('Error getMethods:', error);
              }
            );
          }
        }
      )
    ),
    { dispatch: false }
  );

  // Efecto para manejar la verificación del código Totp
  verifyTotp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(verifyTotp),
      switchMap(action => 
        this.authService.postVerifyMethod({ value: action.code } as PostVerifyAuthRequest).pipe(
          first(),
          map(response => {
            this.store.dispatch(updateAuthMethods({ authMethods: null }));
            this.store.dispatch(updateTotpRegisterAuth({ totpRegisterAuth: null }));
            return loginSuccess({ user: response })
          }),
          catchError(error => {
            this.store.dispatch(updateVerifyTotpInProcess({ verifyTotpInProcess: false }));
            return of(verifyMethodError({ verifyMethodError: !!error.error ? `${error.error}` : null }))
          })
        )
      )
    )
  );

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logout),
      switchMap(action => 
        this.authService.postLogout().pipe(
          first(),
          map(() => logoutSuccess()),
          catchError(error => of(logoutError({ error })))
        )
      )
    )
  );

  logoutError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logoutError),
      map(action => updateError({ error: action.error }))
    ),
  );

  logoutAction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logoutSuccess, logoutError),
      map(
        action => {
          this.router.navigate([Paths.login]);
          this.store.dispatch(updateLoginInProcess({ loginInProcess: false }));
          this.store.dispatch(updateVerifyTotpInProcess({ verifyTotpInProcess: false }));
          this.store.dispatch(updateAuthMethods({ authMethods: null }));
          this.store.dispatch(verifyMethodError({ verifyMethodError: null }));
          this.store.dispatch(updateTotpRegisterAuth({ totpRegisterAuth: null }));
          this.store.dispatch(resetError());
          if (typeof window !== 'undefined') {
            localStorage.removeItem(tokenL);
            localStorage.removeItem(refreshL);
          }
        }
      )
    ),
    { dispatch: false }
  );

  updateTokens$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateTokens),
      map(
        action => {
          if (typeof window !== 'undefined') {
            localStorage.setItem(tokenL, action.token ?? '');
            localStorage.setItem(refreshL, action.refreshToken ?? '');
          }
        }
      ),
    ),
    { dispatch: false }
  );

  refreshTokenSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refreshTokenSuccess),
      map(action => updateTokens({ token: action.token, refreshToken: action.refreshToken }))
    )
  );

  refreshTokenError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(refreshTokenError),
      map(
        action => {
          this.store.dispatch(logoutSuccess());
          return updateError({ error: action.error });
        }
      )
    ),
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePassword),
      switchMap(action =>
        this.usuariosService.postChangePassword(action.postChangePasswordRequest).pipe(
          first(),
          map(
            () => {
              this.alertService.showBasicMessage(`¡Contraseña cambiada correctamente!`, ``, "success", true);
              return logoutSuccess();
            }
          ),
          catchError(
            error => {
              this.alertService.showHtmlMessage(`¡Error!`, JSON.parse(error.error), "error", true);
              if (error.status !== 400) {
                this.store.dispatch(updateLoginInProcess({ loginInProcess: false }));
                this.store.dispatch(updateVerifyTotpInProcess({ verifyTotpInProcess: false }));
                this.router.navigate([Paths.login]);
              }
              return of(changePasswordError({ error }))
            }
          )
        )
      )
    )
  );

  changePassError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(changePasswordError),
      map(action => updateError({ error: action.error }))
    ),
  );

  recoverPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(recoverPassword),
      switchMap(action =>
        this.usuariosService.postRecoverPassword(action.email).pipe(
          first(),
          map(
            () => {
              this.alertService.showBasicMessage(`¡Petición procesada!`, `Revise su cuenta de correo electrónico. En breve, le llegará un correo con un enlace para restablecer su contraseña`, "success", true);
              return recoverPasswordSuccess();
            }
          ),
          catchError(
            error => {
              this.alertService.showHtmlMessage(`¡Error!`, JSON.parse(error.error), "error", true);
              this.router.navigate([Paths.login]);
              return of(recoverPasswordError({ error }))
            }
          )
        )
      )
    )
  );
}